import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Elements, useStripe } from '@stripe/react-stripe-js'
import styled from 'styled-components'

import StripeHelpers from 'helpers/stripe_helpers'
import AddCardForm from 'components/views/logged_in/menu/payment_settings/add_card/add_card_form'
import { colors } from 'helpers/colors'
import ErrorsHelper from 'helpers/errors_helper'

const stripePromise = StripeHelpers.loadStripe()

export const stripeElementsAppearance = {
  theme: 'flat',
  variables: {
    border: 'none',
    colorPrimary: colors.tertiary[500],
    colorText: colors.gray[700],
    colorTextSecondary: colors.gray[400],
    colorDanger: colors.primary[500],
    fontFamily: 'Gotham Pro, sans-serif',
    spacingUnit: '3px',
    borderRadius: '8px',
  },
  rules: { '.AccordionItem': { border: 'none', paddingLeft: '3px', paddingRight: '3px' } },
}

function AddCardInner({ loading, onCardAdded, onError, onCancel, setLoading, submitLabel, border }) {
  const stripe = useStripe()

  const handleSavePaymentMethod = useCallback(
    async (elements) => {
      setLoading(true)
      try {
        const { error, setupIntent } = await stripe.confirmSetup({ elements, redirect: 'if_required' })
        if (error) return onError(error.message || 'Failed to save payment method')
        if (['succeeded', 'requires_action', 'processing'].includes(setupIntent?.status)) {
          const isAsync = ['AsyncFunction', 'Promise'].includes(onCardAdded.constructor.name)
          return isAsync ? await onCardAdded(setupIntent) : onCardAdded(setupIntent)
        }

        onError(`Unexpected status: ${setupIntent?.status || 'unknown'}`)
      } finally {
        setLoading(false)
      }
    },
    [stripe, onCardAdded, onError, setLoading]
  )

  return (
    <AddCardForm
      border={border}
      submitLabel={submitLabel}
      onCancel={onCancel}
      disabled={loading}
      onSubmit={handleSavePaymentMethod}
      loading={loading}
    />
  )
}

const AddCard = ({ onError, onCardAdded, onCancel, submitLabel, border, bookeePaymentCapabilities }) => {
  const [loading, setLoading] = useState(true)
  const [clientSecret, setClientSecret] = useState(null)
  const options = useMemo(
    () => ({
      // payment_method_types: ['card'],
      // mode: 'setup',
      clientSecret,
      appearance: stripeElementsAppearance,
    }),
    [clientSecret]
  )

  const handleConfigureSetupIntent = useCallback(async () => {
    setLoading(true)
    try {
      await StripeHelpers.ensureStripeCustomer()
      const setupIntent = await StripeHelpers.createSetupIntent(bookeePaymentCapabilities)
      setClientSecret(setupIntent.client_secret)
    } catch (error) {
      onError(ErrorsHelper.normalizeError(error, 'Unable to initialize payment method. Please try again later.'))
    } finally {
      setLoading(false)
    }
  }, [setLoading, onError, bookeePaymentCapabilities])

  useEffect(() => {
    handleConfigureSetupIntent()
  }, [handleConfigureSetupIntent])

  return (
    <Wrapper id="AddCardWrapper">
      {clientSecret && (
        <Elements stripe={stripePromise} options={options}>
          <AddCardInner
            loading={loading}
            border={border}
            submitLabel={submitLabel}
            onError={onError}
            onCardAdded={onCardAdded}
            onCancel={onCancel}
            setLoading={setLoading}
          />
        </Elements>
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
  min-height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  @media (max-width: 768px) {
    display: block;
    min-height: unset;
    height: unset;
  }
`

export default AddCard
